<template>
  <div class="home">
    <div class="hero">
      <h1>Tesla referral: 1,000 km of free Supercharging</h1>
      <p class="subtitle">Order your new Model 3 or Model Y with my official referral link and get 1,000 km of free charging on the Supercharger network. No extra cost for you.</p>
      <a :href="referralLink" target="_blank" rel="noopener noreferrer" class="cta-button">⚡ Use the referral link</a>
      <p class="hero-note">Offer checked on {{ verifiedOn }} · Official Tesla program · No code to enter</p>
    </div>

    <div class="referral-section">
      <div class="warning">
        <strong>Important:</strong> click the link BEFORE you start your order on tesla.com. Tesla does not apply a referral to an order that has already been placed.
      </div>

      <div class="availability" :class="{ low: referralsLeft <= 3 }">
        <div class="availability-text">
          <strong>{{ referralsLeft > 0 ? 'Referral available' : 'No referral left this year' }}</strong>
          <span>{{ referralsLeft }} / {{ referralsMax }} referrals left this year</span>
        </div>
        <div class="availability-bar" role="progressbar" :aria-valuenow="referralsLeft" aria-valuemin="0" :aria-valuemax="referralsMax">
          <div class="availability-fill" :style="{ width: (referralsLeft / referralsMax) * 100 + '%' }"></div>
        </div>
      </div>

      <div class="steps">
        <h2>How it works</h2>
        <ol>
          <li><strong>Click the link</strong> above: it opens tesla.com with the referral already attached, nothing to type.</li>
          <li><strong>Configure and order</strong> your Tesla as usual, at the same price.</li>
          <li><strong>Your 1,000 km of Supercharging</strong> show up in your Tesla account after delivery.</li>
        </ol>
      </div>

      <div class="benefits">
        <h2>Good to know</h2>
        <ul>
          <li>⚡ 1,000 km of free charging on Tesla Superchargers (about 150 kWh)</li>
          <li>🚗 New Model 3 and Model Y only, ordered through the link</li>
          <li>💶 No extra cost: the price of your Tesla does not change, and the referral stacks with public incentives</li>
          <li>✅ One referral per buyer, capped at 10 per referrer per calendar year</li>
          <li>⏳ Tesla can change or end the program at any time: the current offer is not guaranteed</li>
        </ul>
      </div>

      <div class="referral-card">
        <h2>My referral link</h2>
        <div class="link-container">
          <input
            ref="linkInput"
            type="text"
            :value="referralLink"
            readonly
            class="referral-link"
            @click="copyToClipboard"
          />
          <button @click="copyToClipboard" class="copy-button" :class="{ copied: isCopied }">
            {{ isCopied ? '✓ Copied!' : 'Copy' }}
          </button>
        </div>
        <p class="link-description">Copy it if you would rather order later: the reward applies as long as the order starts from this link.</p>
      </div>

      <p class="trust">I'm Damien, a Tesla owner since August 2023 with 32,000 km on the clock. This is my personal referral link: by using it you get your 1,000 km and so do I. Thank you!</p>

      <div class="faq">
        <h2>Frequently asked questions</h2>
        <details v-for="item in faq" :key="item.question">
          <summary>{{ item.question }}</summary>
          <p>{{ item.answer }}</p>
        </details>
      </div>

      <div class="final-cta">
        <a :href="referralLink" target="_blank" rel="noopener noreferrer" class="cta-button">⚡ Use the referral link</a>
      </div>

      <div class="share-buttons">
        <h3>Send to a friend</h3>
        <div class="share-options">
          <button @click="shareEmail" class="share-button email">📧 Email</button>
          <button @click="shareWhatsApp" class="share-button whatsapp">💬 WhatsApp</button>
          <button @click="shareTwitter" class="share-button twitter">✖️ X</button>
        </div>
      </div>

      <p class="disclaimer">Independent personal site, not affiliated with Tesla, Inc. Orders are placed exclusively on tesla.com; the referral program terms are those published by Tesla.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useSEO, type FAQItem } from '../composables/useSEO'

const referralLink = 'https://ts.la/damien430515'
const linkInput = ref<HTMLInputElement | null>(null)
const isCopied = ref(false)

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(referralLink)
    isCopied.value = true
    setTimeout(() => {
      isCopied.value = false
    }, 2000)
  } catch (err) {
    if (linkInput.value) {
      linkInput.value.select()
      document.execCommand('copy')
      isCopied.value = true
      setTimeout(() => {
        isCopied.value = false
      }, 2000)
    }
  }
}

const shareEmail = () => {
  const subject = encodeURIComponent('Tesla referral: 1,000 km of free Supercharging')
  const body = encodeURIComponent(`Order your Tesla with this referral link and get 1,000 km of free Supercharging, at no extra cost:\n\n${referralLink}`)
  window.location.href = `mailto:?subject=${subject}&body=${body}`
}

const shareWhatsApp = () => {
  const text = encodeURIComponent(`1,000 km of free Supercharging if you order your Tesla with this referral link: ${referralLink}`)
  window.open(`https://wa.me/?text=${text}`, '_blank')
}

const shareTwitter = () => {
  const text = encodeURIComponent(`Tesla referral: 1,000 km of free Supercharging on your order ${referralLink}`)
  window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank')
}

const faq: FAQItem[] = [
  {
    question: 'Do I need to enter a Tesla referral code?',
    answer: 'No. Tesla referrals work with a link, not a code. Click the link and it opens tesla.com with the referral already attached to your order.'
  },
  {
    question: 'When should I click the link?',
    answer: 'Before you start configuring your Tesla on tesla.com, in the same session. Tesla does not attach a referral to an order that has already been placed.'
  },
  {
    question: 'What is the reward right now?',
    answer: '1,000 km of free charging on Tesla Superchargers for a new Model 3 or Model Y ordered through the link. Tesla adjusts the program several times a year.'
  },
  {
    question: 'How do I check that the referral was applied?',
    answer: 'In your Tesla account under Referral / Loot Box once the order is placed, then in your charging credits after delivery.'
  },
  {
    question: 'Does the referral stack with public EV incentives?',
    answer: 'Yes. The referral is a Tesla reward independent from public incentives, both apply.'
  },
  {
    question: 'Does this site take my order?',
    answer: 'No. This site only shares a referral link. Ordering, payment and delivery happen exclusively on tesla.com.'
  }
]

// Date of the last check of the program terms shown on the page
const verifiedOn = '12 September 2026'
// Tesla caps order referrals at 10 per referrer per calendar year: update after each successful referral
const referralsMax = 10
const referralsLeft = 7

useSEO({
  faq,
  lang: 'en',
  title: 'Tesla referral: 1,000 km of free Supercharging',
  description: 'Order your Tesla with my official referral link and get 1,000 km of free charging on Superchargers. No extra cost.'
})
</script>

<style scoped>
.home {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.hero {
  text-align: center;
  margin-bottom: 3rem;
}

h1 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle {
  font-size: 1.25rem;
  color: #666;
  margin-bottom: 2rem;
}

.referral-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.referral-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  color: white;
}

.referral-card h2 {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: white;
}

.link-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.referral-link {
  flex: 1;
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.referral-link:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.referral-link::selection {
  background: rgba(255, 255, 255, 0.3);
}

.copy-button {
  padding: 1rem 2rem;
  border: 2px solid white;
  border-radius: 8px;
  background: white;
  color: #667eea;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.copy-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.copy-button.copied {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.link-description {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 0.95rem;
}

.benefits {
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 12px;
  border-left: 4px solid #667eea;
}

.benefits h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.benefits ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefits li {
  padding: 0.75rem 0;
  font-size: 1.1rem;
  color: #333;
  line-height: 1.6;
}

.share-buttons {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.share-buttons h3 {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #1a1a1a;
  text-align: center;
}

.share-options {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.share-button {
  padding: 1rem 2rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
}

.share-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.share-button.email:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.share-button.whatsapp:hover {
  border-color: #25d366;
  color: #25d366;
}

.share-button.twitter:hover {
  border-color: #1da1f2;
  color: #1da1f2;
}

@media (max-width: 768px) {
  h1 {
    font-size: 2rem;
  }

  .link-container {
    flex-direction: column;
  }

  .copy-button {
    width: 100%;
  }

  .share-options {
    flex-direction: column;
  }

  .share-button {
    width: 100%;
  }
}

.cta-button {
  display: inline-block;
  padding: 1.1rem 2.5rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.35);
  transition: all 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 34px rgba(102, 126, 234, 0.45);
}

.steps {
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 12px;
  border-left: 4px solid #764ba2;
}

.steps h2 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.steps ol {
  margin: 0;
  padding-left: 1.5rem;
  line-height: 1.7;
}

.steps li {
  padding: 0.35rem 0;
}

.trust {
  color: #555;
  line-height: 1.6;
  text-align: center;
  font-style: italic;
}

.hero-note {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #777;
}

.warning {
  background: #fff7ed;
  border-left: 4px solid #f59e0b;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  color: #7c2d12;
  line-height: 1.6;
}

.benefits h2 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.faq h2 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.faq details {
  border-bottom: 1px solid #e5e7eb;
  padding: 0.75rem 0;
}

.faq summary {
  cursor: pointer;
  font-weight: 600;
  color: #1a1a1a;
}

.faq details p {
  margin: 0.75rem 0 0;
  color: #444;
  line-height: 1.6;
}

.final-cta {
  text-align: center;
}

.disclaimer {
  font-size: 0.8rem;
  color: #888;
  text-align: center;
  line-height: 1.5;
}

.availability {
  background: #ecfdf5;
  border-left: 4px solid #10b981;
  padding: 1rem 1.5rem;
  border-radius: 12px;
}

.availability.low {
  background: #fff7ed;
  border-left-color: #f59e0b;
}

.availability-text {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
  color: #1a1a1a;
}

.availability-text span {
  color: #555;
  font-size: 0.95rem;
}

.availability-bar {
  height: 8px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  overflow: hidden;
}

.availability-fill {
  height: 100%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.availability.low .availability-fill {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}
</style>

